import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Space, Typography, Progress, Statistic, Alert, Divider } from 'antd';
import {
  DollarOutlined,
  PieChartOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { validateCampaignBudgets } from '../utils/campaignValidation';

const { Text, Title } = Typography;

/**
 * CampaignBudgetAllocation Component
 *
 * Visual budget distribution widget showing allocation across campaigns.
 * Features:
 * - Debounced calculation to prevent flickering on keystroke changes (500ms delay)
 * - Color-coded progress bar for budget utilization
 * - Warning if >20% unallocated
 * - Campaign-by-campaign breakdown
 * - Budget validation feedback
 *
 * Props:
 * @param {Array} campaigns - Array of campaign objects with budget and name
 * @param {number} orderBudget - Total order budget
 */
const CampaignBudgetAllocation = React.memo(({ campaigns = [], orderBudget = 0 }) => {
  // Debounced values to prevent recalculation on every keystroke
  const [debouncedCampaigns, setDebouncedCampaigns] = useState(campaigns);
  const [debouncedOrderBudget, setDebouncedOrderBudget] = useState(orderBudget);
  // Track whether the first debounce cycle has completed so we don't flash
  // misleading "At least one campaign is required" during initial form load.
  const [hasSettled, setHasSettled] = useState(false);

  // Debounce budget calculations - wait 500ms after changes stop
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedCampaigns(campaigns);
      setDebouncedOrderBudget(orderBudget);
      setHasSettled(true);
    }, 500);

    // Cleanup: cancel the timeout if values change again before 500ms
    return () => clearTimeout(timeoutId);
  }, [campaigns, orderBudget]);

  // Calculate budget metrics using debounced values
  const budgetMetrics = useMemo(() => {
    const validation = validateCampaignBudgets(debouncedCampaigns, debouncedOrderBudget);

    const totalAllocated = debouncedCampaigns.reduce(
      (sum, campaign) => sum + (parseFloat(campaign.budget) || 0),
      0
    );

    const remaining = debouncedOrderBudget - totalAllocated;
    const utilizationPercent = debouncedOrderBudget > 0 ? (totalAllocated / debouncedOrderBudget) * 100 : 0;

    // Calculate per-campaign percentages
    const campaignAllocations = debouncedCampaigns.map((campaign, index) => {
      const budget = parseFloat(campaign.budget) || 0;
      const percentage = debouncedOrderBudget > 0 ? (budget / debouncedOrderBudget) * 100 : 0;

      return {
        index,
        name: campaign.name || `Campaign ${index + 1}`,
        budget,
        percentage,
      };
    });

    return {
      totalAllocated,
      remaining,
      utilizationPercent,
      campaignAllocations,
      validation,
    };
  }, [debouncedCampaigns, debouncedOrderBudget]);

  // Determine status color based on utilization
  const getStatusColor = () => {
    const { utilizationPercent, validation } = budgetMetrics;

    if (!validation.valid) return 'red';
    if (utilizationPercent >= 100) return 'green';
    if (utilizationPercent >= 80) return 'blue';
    if (utilizationPercent >= 50) return 'orange';
    return 'red';
  };

  // Colors for campaign bars
  const campaignColors = [
    '#1890ff', // blue
    '#52c41a', // green
    '#fa8c16', // orange
    '#eb2f96', // magenta
    '#722ed1', // purple
    '#13c2c2', // cyan
    '#faad14', // gold
    '#f5222d', // red
    '#2f54eb', // geekblue
    '#a0d911', // lime
  ];

  const { totalAllocated, remaining, utilizationPercent, campaignAllocations, validation } = budgetMetrics;

  return (
    <Card
      title={
        <Space>
          <PieChartOutlined />
          <Text>Campaign Budget Allocation</Text>
          {!hasSettled && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <LoadingOutlined spin style={{ marginRight: 4 }} />
              Calculating...
            </Text>
          )}
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Summary Statistics */}
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Order Budget"
              value={orderBudget}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Allocated"
              value={totalAllocated}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{
                color: validation.valid ? '#52c41a' : '#f5222d',
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Remaining"
              value={remaining}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{
                color: remaining >= 0 ? '#595959' : '#f5222d',
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Utilization"
              value={utilizationPercent}
              precision={0}
              suffix="%"
              valueStyle={{
                color: getStatusColor(),
              }}
            />
          </Col>
        </Row>

        {/* Overall Progress Bar */}
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Overall Budget Utilization
          </Text>
          <Progress
            percent={Math.min(utilizationPercent, 100)}
            status={validation.valid ? (utilizationPercent >= 80 ? 'success' : 'normal') : 'exception'}
            strokeColor={getStatusColor()}
            showInfo={true}
            format={(percent) => `${percent.toFixed(0)}%`}
          />
        </div>

        {/* Validation Feedback */}
        {hasSettled && !validation.valid && validation.error && (
          <Alert
            type="error"
            message="Budget Allocation Error"
            description={validation.error}
            showIcon
            icon={<WarningOutlined />}
          />
        )}

        {validation.valid && validation.warning && (
          <Alert
            type="warning"
            message="Budget Utilization Warning"
            description={validation.warning}
            showIcon
            icon={<WarningOutlined />}
          />
        )}

        {validation.valid && !validation.warning && utilizationPercent >= 80 && (
          <Alert
            type="success"
            message="Budget Well Allocated"
            description={`${utilizationPercent.toFixed(0)}% of order budget is allocated across campaigns.`}
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ color: '#52c41a' }}
          />
        )}

        {/* Campaign-by-Campaign Breakdown */}
        {debouncedCampaigns.length > 0 && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <div>
              <Text strong style={{ display: 'block', marginBottom: 12 }}>
                Budget Distribution by Campaign
              </Text>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {campaignAllocations.map((campaign, idx) => (
                  <div key={campaign.index}>
                    <Row justify="space-between" style={{ marginBottom: 4 }}>
                      <Col>
                        <Space size="small">
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              backgroundColor: campaignColors[idx % campaignColors.length],
                              borderRadius: '2px',
                            }}
                          />
                          <Text style={{ fontSize: 13 }}>
                            {campaign.name}
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        <Space size="large">
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            {campaign.percentage.toFixed(1)}%
                          </Text>
                          <Text strong style={{ fontSize: 13 }}>
                            ${campaign.budget.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Text>
                        </Space>
                      </Col>
                    </Row>
                    <Progress
                      percent={campaign.percentage}
                      strokeColor={campaignColors[idx % campaignColors.length]}
                      showInfo={false}
                      size="small"
                      style={{ marginBottom: 8 }}
                    />
                  </div>
                ))}
              </Space>
            </div>
          </>
        )}

        {debouncedCampaigns.length === 0 && (
          <Alert
            type="info"
            message="No Campaigns"
            description="Add campaigns to see budget allocation breakdown."
            showIcon
          />
        )}
      </Space>
    </Card>
  );
});

CampaignBudgetAllocation.displayName = 'CampaignBudgetAllocation';

export default CampaignBudgetAllocation;
